import { storableError } from '../../util/errors';
import { convertUnitToSubUnit, unitDivisor } from '../../util/currency';
import {
  parseDateFromISO8601,
  getExclusiveEndDate,
  addTime,
  subtractTime,
  daysBetween,
  getStartOf,
} from '../../util/dates';
import { createImageVariantConfig } from '../../util/sdkLoader';
import {
  boundsToArray,
  isOriginInUse,
  isStockInUse,
  SEARCH_PAGE_COMPANY_VIEW_PARAM,
} from '../../util/search';
import { parse } from '../../util/urlHelpers';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { getCurrentLocale } from '../../util/locale';
import { isMarketplaceSecondLife } from '../../util/marketplace';
import api from '../../api';
import { parseListingToSharetribe, parseOurResponseToST } from '../../util/sharetribeParser';
import {
  getListingLocalesForUserRole,
  isUserVerified,
  isUserVerifiedCompanyDealer,
} from '../../util/user';
import { queryCompanies } from '../../ducks/company.duck';
import { PRODUCT_TYPE_QUERY_MAP } from '../../constants/listing';

// Pagination page size might need to be dynamic on responsive page layouts
// Current design has max 3 columns 12 is divisible by 2 and 3
// So, there's enough cards to fill all columns on full pagination pages
const RESULT_PAGE_SIZE_2ND_LIFE = 48;
const RESULT_PAGE_SIZE_RESPARE = 24;

// ================ Action types ================ //

const FETCH_COMPANY_REQUEST = 'app/SearchPage/FETCH_COMPANY_REQUEST';
const FETCH_COMPANY_SUCCESS = 'app/SearchPage/FETCH_COMPANY_SUCCESS';
const FETCH_COMPANY_ERROR = 'app/SearchPage/FETCH_COMPANY_ERROR';

export const SEARCH_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_LISTINGS_REQUEST';
export const SEARCH_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_LISTINGS_SUCCESS';
export const SEARCH_LISTINGS_ERROR = 'app/SearchPage/SEARCH_LISTINGS_ERROR';

export const SEARCH_MAP_LISTINGS_REQUEST = 'app/SearchPage/SEARCH_MAP_LISTINGS_REQUEST';
export const SEARCH_MAP_LISTINGS_SUCCESS = 'app/SearchPage/SEARCH_MAP_LISTINGS_SUCCESS';
export const SEARCH_MAP_LISTINGS_ERROR = 'app/SearchPage/SEARCH_MAP_LISTINGS_ERROR';

export const SEARCH_MAP_SET_ACTIVE_LISTING = 'app/SearchPage/SEARCH_MAP_SET_ACTIVE_LISTING';

const FETCH_MENU_COUNTS_REQUEST = 'app/SearchPage/FETCH_MENU_COUNTS_REQUEST';
const FETCH_MENU_COUNTS_SUCCESS = 'app/SearchPage/FETCH_MENU_COUNTS_SUCCESS';
const FETCH_MENU_COUNTS_ERROR = 'app/SearchPage/FETCH_MENU_COUNTS_ERROR';

const FETCH_ARCHIVED_COUNTS_REQUEST = 'app/SearchPage/FETCH_ARCHIVED_COUNTS_REQUEST';
const FETCH_ARCHIVED_COUNTS_SUCCESS = 'app/SearchPage/FETCH_ARCHIVED_COUNTS_SUCCESS';
const FETCH_ARCHIVED_COUNTS_ERROR = 'app/SearchPage/FETCH_ARCHIVED_COUNTS_ERROR';

const FETCH_FOR_SALE_COUNTS_REQUEST = 'app/SearchPage/FETCH_FOR_SALE_COUNTS_REQUEST';
const FETCH_FOR_SALE_COUNTS_SUCCESS = 'app/SearchPage/FETCH_FOR_SALE_COUNTS_SUCCESS';
const FETCH_FOR_SALE_COUNTS_ERROR = 'app/SearchPage/FETCH_FOR_SALE_COUNTS_ERROR';

const FETCH_BUYING_COUNTS_REQUEST = 'app/SearchPage/FETCH_BUYING_COUNTS_REQUEST';
const FETCH_BUYING_COUNTS_SUCCESS = 'app/SearchPage/FETCH_BUYING_COUNTS_SUCCESS';
const FETCH_BUYING_COUNTS_ERROR = 'app/SearchPage/FETCH_BUYING_COUNTS_ERROR';

const FETCH_SELLING_COUNTS_REQUEST = 'app/SearchPage/FETCH_SELLING_COUNTS_REQUEST';
const FETCH_SELLING_COUNTS_SUCCESS = 'app/SearchPage/FETCH_SELLING_COUNTS_SUCCESS';
const FETCH_SELLING_COUNTS_ERROR = 'app/SearchPage/FETCH_SELLING_COUNTS_ERROR';

const FETCH_CONTRACT_EXPIRATION_COUNTS_REQUEST =
  'app/SearchPage/FETCH_CONTRACT_EXPIRATION_COUNTS_REQUEST';
const FETCH_CONTRACT_EXPIRATION_COUNTS_SUCCESS =
  'app/SearchPage/FETCH_CONTRACT_EXPIRATION_COUNTS_SUCCESS';
const FETCH_CONTRACT_EXPIRATION_COUNTS_ERROR =
  'app/SearchPage/FETCH_CONTRACT_EXPIRATION_COUNTS_ERROR';

const FETCH_MODELS_REQUEST = 'app/SearchPage/FETCH_MODELS_REQUEST';
const FETCH_MODELS_SUCCESS = 'app/SearchPage/FETCH_MODELS_SUCCESS';
const FETCH_MODELS_ERROR = 'app/SearchPage/FETCH_MODELS_ERROR';

// ================ Reducer ================ //

const initialState = {
  pagination: null,
  searchParams: null,
  searchInProgress: false,
  searchListingsError: null,
  currentPageResultIds: [],

  queryCompanyInProgress: false,
  queryCompanyError: null,
  company: null,

  menuCounts: {},
  menuCountsError: null,

  archivedCounts: {},
  archivedCountsError: null,

  forSaleCounts: {},
  forSaleCountsError: null,

  buyingAsIsCounts: {},
  buyingAsIsCountsError: null,

  sellingAsIsCounts: {},
  sellingAsIsCountsError: null,

  contractExpirationCounts: {},
  contractExpirationCountsError: null,

  models: [],
  fetchModelsInProgress: false,
  fetchModelsError: null,
};

const resultIds = data => {
  if (!data || !Array.isArray(data.data)) return [];
  return data.data.map(l => l.id);
};

const asArray = obj => {
  if (!obj) return [];
  if (Array.isArray(obj)) return obj;
  if (Array.isArray(obj.data)) return obj.data;
  if (Array.isArray(obj?.data?.data)) return obj.data.data;
  if (Array.isArray(obj.results)) return obj.results;
  return [];
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SEARCH_LISTINGS_REQUEST:
      return {
        ...state,
        searchParams: payload.searchParams,
        searchInProgress: true,
        searchMapListingIds: [],
        searchListingsError: null,
      };
    case SEARCH_LISTINGS_SUCCESS:
      return {
        ...state,
        currentPageResultIds: resultIds(payload.data),
        pagination: payload.data.meta,
        searchInProgress: false,
      };
    case SEARCH_LISTINGS_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, searchInProgress: false, searchListingsError: payload };

    case SEARCH_MAP_SET_ACTIVE_LISTING:
      return {
        ...state,
        activeListingId: payload,
      };

    case FETCH_COMPANY_REQUEST:
      return {
        ...state,
        company: null,
        queryCompanyInProgress: true,
        queryCompanyError: null,
      };
    case FETCH_COMPANY_SUCCESS:
      return {
        ...state,
        queryCompanyInProgress: false,
        company: payload,
      };
    case FETCH_COMPANY_ERROR:
      // eslint-disable-next-line no-console
      console.error(payload);
      return { ...state, queryCompanyInProgress: false, queryCompanyError: payload };

    case FETCH_MENU_COUNTS_REQUEST:
      return { ...state, menuCountsError: null };
    case FETCH_MENU_COUNTS_SUCCESS:
      return { ...state, menuCounts: payload };
    case FETCH_MENU_COUNTS_ERROR:
      return { ...state, menuCountsError: payload };

    case FETCH_ARCHIVED_COUNTS_REQUEST:
      return { ...state, archivedCountsError: null };
    case FETCH_ARCHIVED_COUNTS_SUCCESS:
      return { ...state, archivedCounts: payload };
    case FETCH_ARCHIVED_COUNTS_ERROR:
      return { ...state, archivedCountsError: payload };

    case FETCH_FOR_SALE_COUNTS_REQUEST:
      return { ...state, forSaleCountsError: null };
    case FETCH_FOR_SALE_COUNTS_SUCCESS:
      return { ...state, forSaleCounts: payload };
    case FETCH_FOR_SALE_COUNTS_ERROR:
      return { ...state, forSaleCountsError: payload };

    case FETCH_BUYING_COUNTS_REQUEST:
      return { ...state, buyingAsIsCountsError: null };
    case FETCH_BUYING_COUNTS_SUCCESS:
      return { ...state, buyingAsIsCounts: payload };
    case FETCH_BUYING_COUNTS_ERROR:
      return { ...state, buyingAsISCountsError: payload };

    case FETCH_SELLING_COUNTS_REQUEST:
      return { ...state, sellingAsIsCountsError: null };
    case FETCH_SELLING_COUNTS_SUCCESS:
      return { ...state, sellingAsIsCounts: payload };
    case FETCH_SELLING_COUNTS_ERROR:
      return { ...state, sellingAsIsCountsError: payload };

    case FETCH_CONTRACT_EXPIRATION_COUNTS_REQUEST:
      return { ...state, contractExpirationCountsError: null };
    case FETCH_CONTRACT_EXPIRATION_COUNTS_SUCCESS:
      return { ...state, contractExpirationCounts: payload };
    case FETCH_CONTRACT_EXPIRATION_COUNTS_ERROR:
      return { ...state, contractExpirationCountsError: payload };

    case FETCH_MODELS_REQUEST:
      return { ...state, fetchModelsInProgress: true, fetchModelsError: null };
    case FETCH_MODELS_SUCCESS:
      return { ...state, fetchModelsInProgress: false, models: action.payload };
    case FETCH_MODELS_ERROR:
      return { ...state, fetchModelsInProgress: false, fetchModelsError: action.payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

const queryCompanyRequest = () => ({
  type: FETCH_COMPANY_REQUEST,
});

const queryCompanySuccess = payload => ({
  type: FETCH_COMPANY_SUCCESS,
  payload,
});

const queryCompanyError = e => ({
  type: FETCH_COMPANY_ERROR,
  error: true,
  payload: e,
});

export const fetchMenuCounts = params => dispatch => {
  dispatch({ type: FETCH_MENU_COUNTS_REQUEST });

  return api.listings
    .getMenuCounts(params)
    .then(menuCountsRes => {
      dispatch({ type: FETCH_MENU_COUNTS_SUCCESS, payload: menuCountsRes.data });
    })
    .catch(e => {
      dispatch({ type: FETCH_MENU_COUNTS_ERROR, payload: e });
    });
};

export const fetchArchivedCounts = params => dispatch => {
  dispatch({ type: FETCH_ARCHIVED_COUNTS_REQUEST });

  return api.listings
    .getArchivedCounts(params)
    .then(archivedCountsRes => {
      dispatch({
        type: FETCH_ARCHIVED_COUNTS_SUCCESS,
        payload: archivedCountsRes.data,
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_ARCHIVED_COUNTS_ERROR, payload: e });
    });
};

export const fetchForSaleCounts = params => dispatch => {
  dispatch({ type: FETCH_FOR_SALE_COUNTS_REQUEST });

  return api.listings
    .getForSaleCounts(params)
    .then(forSaleCountsRes => {
      dispatch({
        type: FETCH_FOR_SALE_COUNTS_SUCCESS,
        payload: forSaleCountsRes.data,
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_FOR_SALE_COUNTS_ERROR, payload: e });
    });
};

export const fetchBuyingAsIsCounts = params => dispatch => {
  dispatch({ type: FETCH_BUYING_COUNTS_REQUEST });

  return api.listings
    .getBuyingAsIsCounts(params)
    .then(buyingCountsRes => {
      dispatch({
        type: FETCH_BUYING_COUNTS_SUCCESS,
        payload: buyingCountsRes.data,
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_BUYING_COUNTS_ERROR, payload: e });
    });
};

export const fetchSellingAsIsCounts = params => dispatch => {
  dispatch({ type: FETCH_SELLING_COUNTS_REQUEST });

  return api.listings
    .getSellingAsIsCounts(params)
    .then(sellingCountsRes => {
      dispatch({
        type: FETCH_SELLING_COUNTS_SUCCESS,
        payload: sellingCountsRes.data,
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_SELLING_COUNTS_ERROR, payload: e });
    });
};

export const fetchContractExpirationCounts = (
  pendingIntakeState,
  normalizedProductType
) => dispatch => {
  dispatch({ type: FETCH_CONTRACT_EXPIRATION_COUNTS_REQUEST });

  let timestampParam = null;
  const now = Date.now();

  if (pendingIntakeState === 'soon') {
    const inSixMonths = new Date();
    inSixMonths.setMonth(inSixMonths.getMonth() + 6);

    timestampParam = `${now},${inSixMonths.getTime()}`;
  } else if (pendingIntakeState === 'expired') {
    timestampParam = `,${now}`;
  }

  const params = {
    pub_contractEndDateTimestamp: timestampParam,
    ...(normalizedProductType ? { pub_productType: normalizedProductType } : {}),
  };

  return api.listings
    .getContractExpirationCounts(params)
    .then(contractExpirationCountsRes => {
      dispatch({
        type: FETCH_CONTRACT_EXPIRATION_COUNTS_SUCCESS,
        payload: contractExpirationCountsRes.data,
      });
    })
    .catch(e => {
      dispatch({ type: FETCH_CONTRACT_EXPIRATION_COUNTS_ERROR, payload: e });
    });
};

export const searchListingsRequest = searchParams => ({
  type: SEARCH_LISTINGS_REQUEST,
  payload: { searchParams },
});

export const searchListingsSuccess = response => ({
  type: SEARCH_LISTINGS_SUCCESS,
  payload: { data: response.data },
});

export const searchListingsError = e => ({
  type: SEARCH_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const searchListings = (searchParams, config) => (dispatch, getState, sdk) => {
  dispatch(searchListingsRequest(searchParams));

  const is2ndLife = isMarketplaceSecondLife(config);

  const { currentUser } = getState().user;

  const isDealer = isUserVerifiedCompanyDealer(currentUser);

  // SearchPage can enforce listing query to only those listings with valid listingType
  // NOTE: this only works if you have set 'enum' type search schema to listing's public data fields
  //       - listingType
  //       Same setup could be expanded to 2 other extended data fields:
  //       - transactionProcessAlias
  //       - unitType
  //       ...and then turned enforceValidListingType config to true in configListing.js
  // Read More:
  // https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
  const searchValidListingTypes = listingTypes => {
    return config.listing.enforceValidListingType
      ? {
          pub_listingType: listingTypes.map(l => l.listingType),
          // pub_transactionProcessAlias: listingTypes.map(l => l.transactionType.alias),
          // pub_unitType: listingTypes.map(l => l.transactionType.unitType),
        }
      : {};
  };

  const priceSearchParams = priceParam => {
    const inSubunits = value => convertUnitToSubUnit(value, unitDivisor(config.currency));
    const values = priceParam ? priceParam.split(',') : [];
    return priceParam && values.length === 2
      ? {
          price: [inSubunits(values[0]), inSubunits(values[1]) + 1].join(','),
        }
      : {};
  };

  const datesSearchParams = datesParam => {
    const searchTZ = 'Etc/UTC';
    const datesFilter = config.search.defaultFilters.find(f => f.key === 'dates');
    const values = datesParam ? datesParam.split(',') : [];
    const hasValues = datesFilter && datesParam && values.length === 2;
    const { dateRangeMode, availability } = datesFilter || {};
    const isNightlyMode = dateRangeMode === 'night';
    const isEntireRangeAvailable = availability === 'time-full';

    // SearchPage need to use a single time zone but listings can have different time zones
    // We need to expand/prolong the time window (start & end) to cover other time zones too.
    //
    // NOTE: you might want to consider changing UI so that
    //   1) location is always asked first before date range
    //   2) use some 3rd party service to convert location to time zone (IANA tz name)
    //   3) Make exact dates filtering against that specific time zone
    //   This setup would be better for dates filter,
    //   but it enforces a UX where location is always asked first and therefore configurability
    const getProlongedStart = date => subtractTime(date, 14, 'hours', searchTZ);
    const getProlongedEnd = date => addTime(date, 12, 'hours', searchTZ);

    const startDate = hasValues ? parseDateFromISO8601(values[0], searchTZ) : null;
    const endRaw = hasValues ? parseDateFromISO8601(values[1], searchTZ) : null;
    const endDate =
      hasValues && isNightlyMode
        ? endRaw
        : hasValues
        ? getExclusiveEndDate(endRaw, searchTZ)
        : null;

    const today = getStartOf(new Date(), 'day', searchTZ);
    const possibleStartDate = subtractTime(today, 14, 'hours', searchTZ);
    const hasValidDates =
      hasValues &&
      startDate.getTime() >= possibleStartDate.getTime() &&
      startDate.getTime() <= endDate.getTime();

    const dayCount = isEntireRangeAvailable ? daysBetween(startDate, endDate) : 1;
    const day = 1440;
    const hour = 60;
    // When entire range is required to be available, we count minutes of included date range,
    // but there's a need to subtract one hour due to possibility of daylight saving time.
    // If partial range is needed, then we just make sure that the shortest time unit supported
    // is available within the range.
    // You might want to customize this to match with your time units (e.g. day: 1440 - 60)
    const minDuration = isEntireRangeAvailable ? dayCount * day - hour : hour;
    return hasValidDates
      ? {
          start: getProlongedStart(startDate),
          end: getProlongedEnd(endDate),
          // Availability can be time-full or time-partial.
          // However, due to prolonged time window, we need to use time-partial.
          availability: 'time-partial',
          // minDuration uses minutes
          minDuration,
        }
      : {};
  };

  const {
    perPage,
    price,
    dates,
    sort,
    bounds,
    pub_locale: localeParam,
    pub_dimrModel,
    pageName,
    pub_productType,
    ...rest
  } = searchParams;

  const priceMaybe = priceSearchParams(price);
  const datesMaybe = datesSearchParams(dates);
  const sortMaybe = sort === config.search.sortConfig.relevanceKey ? {} : { sort };
  const locale = getCurrentLocale();

  const paramsNeedingParsing = is2ndLife
    ? {
        bounds: bounds && boundsToArray(bounds),
      }
    : { bounds };

  const userRoleLocales = getListingLocalesForUserRole(currentUser);

  const secondLifeLocale = localeParam || (userRoleLocales?.length ? userRoleLocales : locale);

  const fetchAllMaybe = searchParams?.view === 'table' ? { fetchAll: true } : {};

  const normalizedProductType =
    pub_productType && PRODUCT_TYPE_QUERY_MAP[pub_productType.toLowerCase()];

  // filtering for ending soon and expired listings
  let timestampParam = null;
  const now = Date.now();

  if (rest?.pendingIntakeState === 'soon') {
    const inSixMonths = new Date();
    inSixMonths.setMonth(inSixMonths.getMonth() + 6);

    timestampParam = `${now},${inSixMonths.getTime()}`;
  } else if (rest?.pendingIntakeState === 'expired') {
    timestampParam = `,${now}`;
  }

  const params = {
    ...rest,
    ...priceMaybe,
    ...datesMaybe,
    ...sortMaybe,
    ...paramsNeedingParsing,
    pub_locale: is2ndLife ? secondLifeLocale : locale,
    ...searchValidListingTypes(config.listing.listingTypes),
    perPage,
    ...(is2ndLife ? { keywords: [rest.keywords, pub_dimrModel] } : {}),
    ...fetchAllMaybe,
    ...(normalizedProductType ? { pub_productType: normalizedProductType } : {}),
    ...(timestampParam ? { pub_contractEndDateTimestamp: timestampParam } : {}),
  };

  const isCompanyView = searchParams?.meta_sellingDealerCompanyId ? true : false;

  // const apiFn = is2ndLife
  //   ? params?.type === 'ownListing'
  //     ? api.listings.getListings
  //     : api.listings.getSearchListings
  //   : sdk.listings.query;

  const apiFn = is2ndLife
    ? isUserVerified(currentUser) && isCompanyView
      ? api.listings.getSearchListings
      : isUserVerified(currentUser) &&
        rest?.sellAsIsState === 'buying' &&
        rest?.asIs &&
        (rest?.tab === 'forSale' || rest?.tab === 'pendingSold')
      ? api.listings.getSearchListings
      : isUserVerified(currentUser)
      ? api.listings.getListings
      : api.listings.getSearchListings
    : sdk.listings.query;

  // If the current user is the dealer, we'll make an additional request to retrieve all listings from his company too
  if (
    is2ndLife &&
    isDealer &&
    pageName === 'SearchPageMyListings' &&
    (!rest?.meta_status || rest?.meta_status === 'pendingIntake')
  ) {
    const companyId = currentUser?.attributes?.profile?.publicData?.companyId;
    return Promise.all([
      apiFn(params),
      apiFn({ ...params, [SEARCH_PAGE_COMPANY_VIEW_PARAM]: companyId }),
    ]).then(([res1, res2]) => {
      const parsedRes1 = parseOurResponseToST(res1.data);
      const parsedRes2 = parseOurResponseToST(res2.data);

      const list1 = asArray(parsedRes1);
      const list2 = asArray(parsedRes2);

      let combinedListings = [...list1, ...list2];

      // Remove possible duplicates here
      const uniqueListingsMap = new Map();
      combinedListings.forEach(listing => {
        uniqueListingsMap.set(listing.id.uuid, listing);
      });
      const uniqueListings = Array.from(uniqueListingsMap.values()).map(parseListingToSharetribe);

      const combinedResponse = {
        ...res1,
        data: {
          ...res1.data,
          data: uniqueListings,
          meta: {
            ...res1.data.meta,
            totalItems: uniqueListings.length,
          },
        },
      };

      dispatch(
        addMarketplaceEntities(combinedResponse, {
          listingFields: config?.listing?.listingFields,
        })
      );
      dispatch(searchListingsSuccess(combinedResponse));
      return combinedResponse;
    });
  }

  return apiFn(params)
    .then(res => {
      let response = res;

      // Case: fetchAll=true, where backend returns plain array
      const isFetchAll = Array.isArray(res.data);
      if (isFetchAll) {
        response = {
          ...res,
          data: {
            data: res.data,
            meta: {
              page: 1,
              perPage: res.data.length,
              totalItems: res.data.length,
            },
          },
        };
      }

      if (is2ndLife) {
        // If it's fetchAll, the data and meta are already formed from the array, so we don't parse again
        if (!isFetchAll) {
          response.data = parseOurResponseToST(res.data);
        }

        let listings = response.data.data;

        if (!response.data?.meta) {
          // Just hardcoded for the demo, the /visible route should be changed so it returns pagination info:
          response.data.meta = {
            page: 1,
            perPage: RESULT_PAGE_SIZE_2ND_LIFE,
            totalItems: listings.length,
          };
        }

        response.data.data = listings.map(parseListingToSharetribe);
      }

      const listingFields = config?.listing?.listingFields;
      const sanitizeConfig = { listingFields };

      dispatch(addMarketplaceEntities(response, sanitizeConfig));
      dispatch(searchListingsSuccess(response));
      return response;
    })
    .catch(e => {
      dispatch(searchListingsError(storableError(e)));
      throw e;
    });
};

export const setActiveListing = listingId => ({
  type: SEARCH_MAP_SET_ACTIVE_LISTING,
  payload: listingId,
});

const fetchModelsRequest = () => ({ type: FETCH_MODELS_REQUEST });
const fetchModelsSuccess = models => ({ type: FETCH_MODELS_SUCCESS, payload: models });
const fetchModelsError = e => ({ type: FETCH_MODELS_ERROR, payload: e, error: true });

export const fetchModels = params => dispatch => {
  dispatch(fetchModelsRequest());

  return api.listings
    .getModels(params)
    .then(res => {
      dispatch(fetchModelsSuccess(res.data));
    })
    .catch(e => {
      dispatch(fetchModelsError(e));
    });
};

// const getCategoriesMaybe = () => dispatch => {
//   return dispatch(getCategories());
// };

const queryCompany = companyId => dispatch => {
  dispatch(queryCompanyRequest());

  return api.companies
    .getCompany(companyId)
    .then(response => {
      dispatch(queryCompanySuccess(response.data));
      return response;
    })
    .catch(e => {
      dispatch(queryCompanyError(e));
    });
};

export const loadData = (params, search, config) => (dispatch, getState) => {
  const queryParams = parse(search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const is2ndLife = isMarketplaceSecondLife(config);

  // Add minStock filter with default value (1), if stock management is in use.
  // This can be overwriten with passed-in query parameters.
  const minStockMaybe = isStockInUse(config) && !is2ndLife ? { minStock: 1 } : {};
  const {
    page = 1,
    address,
    origin,
    view,
    [SEARCH_PAGE_COMPANY_VIEW_PARAM]: companyIdParam,
    ...rest
  } = queryParams;

  const {
    menuCounts,
    archivedCounts,
    forSaleCounts,
    buyingAsIsCounts,
    sellingAsIsCounts,
  } = getState().SearchPage;

  const { isAuthenticated } = getState().auth;

  const originMaybe = isOriginInUse(config) && origin ? { origin } : {};

  const isListView = view === 'list';

  const listDataMaybe = isListView ? ['publicData.location'] : [];

  const userImageMaybe = isListView ? ['author.profileImage'] : [];

  const userImageVariantsMaybe = isListView ? ['variants.square-small'] : [];

  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;
  const aspectRatio = aspectHeight / aspectWidth;

  if (is2ndLife) {
    if (getState().auth.isAuthenticated) dispatch(queryCompanies());
    else dispatch(queryCompanies({ locale: 'no' }));
    const { pub_productType } = rest;
    const locale = getCurrentLocale();

    const normalizedProductType =
      pub_productType && PRODUCT_TYPE_QUERY_MAP[pub_productType.toLowerCase()];

    const additionalParams = normalizedProductType
      ? { pub_productType: normalizedProductType }
      : {};

    const searchParamsForModels = {
      ...rest,
      ...originMaybe,
      ...minStockMaybe,
      page,
      perPage: RESULT_PAGE_SIZE_2ND_LIFE,
      view,
      // for public view pass isPublicView and locale
      ...(!getState().auth.isAuthenticated && { isPublicView: true }),
      ...(!getState().auth.isAuthenticated && { pub_locale: locale }),
      ...additionalParams,
      [SEARCH_PAGE_COMPANY_VIEW_PARAM]: companyIdParam,
    };

    dispatch(fetchModels(searchParamsForModels));

    if (isAuthenticated && Object.keys(menuCounts)?.length === 0) {
      dispatch(fetchMenuCounts(additionalParams));
    }

    if (rest?.archived && Object.keys(archivedCounts)?.length === 0) {
      dispatch(fetchArchivedCounts(additionalParams));
    } else if (rest?.forSale && Object.keys(forSaleCounts)?.length === 0) {
      dispatch(fetchForSaleCounts(additionalParams));
    } else if (
      rest?.asIs &&
      rest?.sellAsIsState === 'buying' &&
      Object.keys(buyingAsIsCounts)?.length === 0
    ) {
      dispatch(fetchBuyingAsIsCounts(additionalParams));
    } else if (
      rest?.asIs &&
      rest?.sellAsIsState === 'selling' &&
      Object.keys(sellingAsIsCounts)?.length === 0
    ) {
      dispatch(fetchSellingAsIsCounts(additionalParams));
    } else if (rest?.meta_status === 'pendingIntake' && rest?.pendingIntakeState) {
      dispatch(fetchContractExpirationCounts(rest?.pendingIntakeState, normalizedProductType));
    }

    const { company, queryCompanyInProgress } = getState().SearchPage;

    // If we have query param, and we haven't fetched the company yet
    if (companyIdParam && companyIdParam !== company?._id && !queryCompanyInProgress)
      dispatch(queryCompany(companyIdParam));
    // Remove company if no query param
    else if (!companyIdParam) {
      dispatch(queryCompanySuccess(null));
    }
  }

  // dispatch(getCategoriesMaybe());

  return searchListings(
    {
      ...minStockMaybe,
      ...rest,
      ...originMaybe,
      view,
      page,
      perPage: is2ndLife ? RESULT_PAGE_SIZE_2ND_LIFE : RESULT_PAGE_SIZE_RESPARE,
      pageName: params?.name,
      ...(!is2ndLife
        ? {
            include: ['author', ...userImageMaybe, 'images'],
            'fields.listing': [
              'title',
              'geolocation',
              'price',
              'publicData.listingType',
              'publicData.transactionProcessAlias',
              'publicData.unitType',
              'publicData.intentType',
              // These help rendering of 'purchase' listings,
              // when transitioning from search page to listing page
              'publicData.pickupEnabled',
              'publicData.shippingEnabled',
              ...listDataMaybe,
            ],
            'fields.user': [
              'profile.displayName',
              'profile.abbreviatedName',
              'profile.publicData.companyName',
            ],
            'fields.image': [
              'variants.scaled-small',
              'variants.scaled-medium',
              `variants.${variantPrefix}`,
              `variants.${variantPrefix}-2x`,
              ...userImageVariantsMaybe,
            ],
            ...createImageVariantConfig(`${variantPrefix}`, 400, aspectRatio),
            ...createImageVariantConfig(`${variantPrefix}-2x`, 800, aspectRatio),
            'limit.images': 1,
          }
        : {
            [SEARCH_PAGE_COMPANY_VIEW_PARAM]: companyIdParam,
          }),
    },
    config
  );
};
